import React, { useState, useMemo, useEffect } from 'react';
import calendarApi from '../api/calendarApi';
import {
  formatRoleLabel,
  isMeetingPast,
  getEffectiveMeetingStatus,
  canJoinMeeting,
  getMsUntilMeetingAccessOpen,
  formatMeetingCountdown,
} from '../api/meetingsApi';
import { getRoleTierLevel } from './ScheduleMeetingModal';
import MeetingCard from './MeetingCard';

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MINI_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatDateKey = (d) => {
  if (!d) return '';
  if (typeof d === 'string') {
    const trimmed = d.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
  }
  const dateObj = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(dateObj.getTime())) return '';
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const DISTINCT_HOST_COLORS = [
  '#2563eb', // Royal Blue
  '#ea580c', // Vibrant Orange
  '#10b981', // Emerald Green
  '#8b5cf6', // Violet Purple
  '#e11d48', // Ruby Pink / Red
  '#0891b2', // Cyan / Cerulean
  '#d97706', // Amber Gold
  '#db2777', // Magenta
  '#4f46e5', // Indigo
  '#059669', // Dark Emerald / Teal
  '#ca8a04', // Bronze Yellow
  '#9333ea', // Deep Purple
];

const getAvatarColor = (identifier = '', index = -1) => {
  if (typeof index === 'number' && index >= 0) {
    return DISTINCT_HOST_COLORS[index % DISTINCT_HOST_COLORS.length];
  }
  if (!identifier) return DISTINCT_HOST_COLORS[0];
  let hash = 5381;
  const str = String(identifier).trim().toLowerCase();
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return DISTINCT_HOST_COLORS[Math.abs(hash) % DISTINCT_HOST_COLORS.length];
};

const getInitials = (name = '') => {
  if (!name) return 'TM';
  return (
    name
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join('') || 'TM'
  );
};

// Timeline hours displayed in the Day View (00:00 to 23:00 / 12 AM to 11 PM)
const TIMELINE_HOURS = [
  { hour: 0, label: '12 AM' },
  { hour: 1, label: '01 AM' },
  { hour: 2, label: '02 AM' },
  { hour: 3, label: '03 AM' },
  { hour: 4, label: '04 AM' },
  { hour: 5, label: '05 AM' },
  { hour: 6, label: '06 AM' },
  { hour: 7, label: '07 AM' },
  { hour: 8, label: '08 AM' },
  { hour: 9, label: '09 AM' },
  { hour: 10, label: '10 AM' },
  { hour: 11, label: '11 AM' },
  { hour: 12, label: '12 PM' },
  { hour: 13, label: '01 PM' },
  { hour: 14, label: '02 PM' },
  { hour: 15, label: '03 PM' },
  { hour: 16, label: '04 PM' },
  { hour: 17, label: '05 PM' },
  { hour: 18, label: '06 PM' },
  { hour: 19, label: '07 PM' },
  { hour: 20, label: '08 PM' },
  { hour: 21, label: '09 PM' },
  { hour: 22, label: '10 PM' },
  { hour: 23, label: '11 PM' },
];

export const compareCalendarEvents = (a, b) => {
  const timeA = a.date instanceof Date ? a.date.getTime() : new Date(a.date).getTime();
  const timeB = b.date instanceof Date ? b.date.getTime() : new Date(b.date).getTime();
  if (timeA !== timeB) {
    return timeA - timeB;
  }

  // When start times are equal, whichever ends first appears in front (shorter duration / earlier end time first)
  const getEndTimeMs = (ev, startTimeMs) => {
    if (ev.rawMeeting?.endTime) {
      const t = new Date(ev.rawMeeting.endTime).getTime();
      if (!isNaN(t)) return t;
    }
    if (ev.rawMeeting?.end_time) {
      const t = new Date(ev.rawMeeting.end_time).getTime();
      if (!isNaN(t)) return t;
    }
    const dur =
      Number(
        ev.durationMinutes || ev.rawMeeting?.durationMinutes || ev.rawMeeting?.duration_minutes
      ) || 30;
    return startTimeMs + dur * 60000;
  };

  const endA = getEndTimeMs(a, timeA);
  const endB = getEndTimeMs(b, timeB);

  if (endA !== endB) {
    return endA - endB;
  }

  return (a.title || '').localeCompare(b.title || '');
};

const formatEventTime = (dateObj) => {
  if (!dateObj) return '';
  const d = dateObj instanceof Date ? dateObj : new Date(dateObj);
  if (isNaN(d.getTime())) return '';
  return d
    .toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase(); // e.g. "01:30 pm", "04:00 pm"
};

export default function MeetingCalendarView({
  meetings = [],
  workspaceId = 'ws-primary',
  currentUser,
  currentMember,
  ownerEmail,
  availabilitySettings,
  onSelectMeeting,
  onOpenScheduleModal,
  onOpenBrief,
  onOpenRecap,
  onReschedule,
  onCancel,
  hosts = [],
  meetingTypes = [],
  canCreateMeeting = true,
  roleTierLevel,
}) {
  const callerRoleTier = useMemo(() => {
    if (roleTierLevel !== undefined && roleTierLevel !== null) return roleTierLevel;
    if (currentUser?.isOwner) return 4;
    if (currentUser?.isOrganizationAdmin) return 3;
    if (currentUser?.isSalesManager) return 2;
    const userRole = currentUser?.workspaceRole || currentUser?.workspace_role || currentUser?.role;
    if (userRole) return getRoleTierLevel(userRole);
    return canCreateMeeting ? 2 : 1;
  }, [roleTierLevel, currentUser, canCreateMeeting]);

  const showTeamHostsSection = callerRoleTier >= 2;

  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [activeViewMode, setActiveViewMode] = useState('month'); // 'month' | 'day' | 'list'
  const [showExternalBusy, setShowExternalBusy] = useState(true);
  const [liveBusySlots, setLiveBusySlots] = useState(null);
  const [selectedExternalEvent, setSelectedExternalEvent] = useState(null);

  const hasConnectedCalendar = useMemo(() => {
    if (liveBusySlots && liveBusySlots.length > 0) return true;
    if (!availabilitySettings) return true; // default when settings not loaded yet
    if (!Array.isArray(availabilitySettings.connectedCalendars)) return true;
    return availabilitySettings.connectedCalendars.some(
      (c) => (c.provider === 'google' || c.provider === 'microsoft') && c.status === 'connected'
    );
  }, [availabilitySettings, liveBusySlots]);

  const connectedGoogleEmail = useMemo(() => {
    const cal = availabilitySettings?.connectedCalendars?.find(
      (c) => (c.provider === 'google' || c.provider === 'microsoft') && c.status === 'connected'
    );
    return cal?.account || ownerEmail || currentUser?.email || 'maurice@lead-huntrix.com';
  }, [availabilitySettings, ownerEmail, currentUser]);

  const connectedCalendarProvider = useMemo(() => {
    const cal = availabilitySettings?.connectedCalendars?.find(
      (c) =>
        (c.provider === 'google' || c.provider === 'microsoft' || c.provider === 'outlook') &&
        c.status === 'connected'
    );
    if (cal?.provider === 'microsoft' || cal?.provider === 'outlook') {
      return 'microsoft';
    }
    if (cal?.provider === 'google') {
      return 'google';
    }
    if (
      liveBusySlots &&
      liveBusySlots.some((s) => s.provider === 'microsoft' || s.provider === 'outlook')
    ) {
      return 'microsoft';
    }
    return 'google';
  }, [availabilitySettings, liveBusySlots]);

  // Accordion expansion states
  const [isHostsOpen, setIsHostsOpen] = useState(true);
  const [isTypesOpen, setIsTypesOpen] = useState(true);

  // Filter out the currently logged-in account itself (e.g. Marcus Yeo) and higher/equal tiers from the Team Reps filter
  const otherTeamHosts = useMemo(() => {
    if (!hosts || hosts.length === 0) return [];

    const myIds = [
      currentUser?.id,
      currentUser?.userId,
      currentUser?.sub,
      currentUser?.user_id,
      currentMember?.id,
      currentMember?.userId,
    ]
      .filter(Boolean)
      .map(String);

    const myEmails = [currentUser?.email, ownerEmail, currentMember?.email]
      .filter(Boolean)
      .map((e) => String(e).trim().toLowerCase());

    const myNames = [
      currentUser?.name,
      currentUser?.fullName,
      currentUser?.displayName,
      currentUser?.username,
      currentUser?.firstName && currentUser?.lastName
        ? `${currentUser.firstName} ${currentUser.lastName}`
        : null,
      currentMember?.name,
      currentMember?.username,
    ]
      .filter(Boolean)
      .map((n) => String(n).trim().toLowerCase());

    return hosts.filter((h) => {
      if (!h) return false;
      const hId = h.id ? String(h.id).trim() : '';
      const hUserId = h.userId ? String(h.userId).trim() : '';
      const hEmail = h.email ? String(h.email).trim().toLowerCase() : '';
      const hName = h.name ? String(h.name).trim().toLowerCase() : '';
      const hUsername = h.username ? String(h.username).trim().toLowerCase() : '';

      // 1. Filter out self (current logged-in user / Marcus Yeo)
      if (myIds.some((id) => id && (id === hId || id === hUserId))) return false;
      if (
        myEmails.some(
          (em) => em && (em === hEmail || (hEmail && (hEmail.includes(em) || em.includes(hEmail))))
        )
      )
        return false;
      if (
        myNames.some(
          (nm) =>
            nm &&
            (nm === hName ||
              nm === hUsername ||
              (hName && (hName.includes(nm) || nm.includes(hName))))
        )
      )
        return false;

      // 2. If caller is Owner (tier 4), also exclude Owner host entry (Marcus's tasks are always shown by default)
      if (callerRoleTier >= 4) {
        const hostTier = getRoleTierLevel(h.rawRole || h.role || h.workspace_role);
        if (hostTier >= 4) return false;
      }

      // 3. If caller is Manager (tier 2) or Admin (tier 3), only show options for tiers strictly lower than caller
      if (callerRoleTier < 4) {
        const hostTier = getRoleTierLevel(h.rawRole || h.role || h.workspace_role);
        if (hostTier >= callerRoleTier) return false;
      }

      return true;
    });
  }, [hosts, currentUser, currentMember, ownerEmail, callerRoleTier]);

  // Unified deterministic color map ensuring sidebar dots and calendar event cards are 100% synchronized with zero conflicts
  const hostColorMap = useMemo(() => {
    const map = new Map();
    const PERSONAL_COLOR = '#2563eb'; // Royal Blue (Reserved exclusively for Personal Schedule / Owner Tasks)
    const EXTERNAL_COLOR = '#ea4335'; // Red (Reserved for External Synced Calendar Tasks: Google & Microsoft)

    // Dedicated high-contrast spectrum for team reps (completely avoiding Personal Blue #2563eb and External Red #ea4335)
    const REP_PALETTE = [
      '#10b981', // Emerald Green
      '#8b5cf6', // Violet Purple
      '#d97706', // Amber Gold
      '#0891b2', // Cyan Cerulean
      '#db2777', // Magenta Pink
      '#059669', // Dark Emerald
      '#ea580c', // Bright Orange
      '#9333ea', // Deep Purple
      '#ca8a04', // Bronze Yellow
      '#4f46e5', // Indigo
    ];

    (otherTeamHosts || []).forEach((host, idx) => {
      const color = REP_PALETTE[idx % REP_PALETTE.length];
      if (host.id) map.set(String(host.id).trim().toLowerCase(), color);
      if (host.userId) map.set(String(host.userId).trim().toLowerCase(), color);
      if (host.email) map.set(String(host.email).trim().toLowerCase(), color);
      if (host.name) map.set(String(host.name).trim().toLowerCase(), color);
      if (host.username) map.set(String(host.username).trim().toLowerCase(), color);
    });

    return {
      getColorForHost: (hostId, hostName, hostEmail, rawMeeting) => {
        const checkKeys = [
          hostId,
          hostName,
          hostEmail,
          rawMeeting?.hostId,
          rawMeeting?.host_id,
          rawMeeting?.hostName,
          rawMeeting?.host_name,
          rawMeeting?.hostEmail,
          rawMeeting?.host_email,
          rawMeeting?.preMeetingBrief?.assignedStaffName,
          rawMeeting?.pre_meeting_brief?.assignedStaffName,
          rawMeeting?.preMeetingBrief?.assignedTo,
          rawMeeting?.pre_meeting_brief?.assignedTo,
        ]
          .filter(Boolean)
          .map((k) => String(k).trim().toLowerCase());

        for (const key of checkKeys) {
          if (map.has(key)) return map.get(key);
          for (const host of otherTeamHosts || []) {
            const hEmail = host.email ? String(host.email).trim().toLowerCase() : '';
            const hName = host.name ? String(host.name).trim().toLowerCase() : '';
            if (hEmail && (hEmail === key || key.includes(hEmail) || hEmail.includes(key))) {
              return map.get(hEmail) || PERSONAL_COLOR;
            }
            if (hName && (hName === key || key.includes(hName) || hName.includes(key))) {
              return map.get(hName) || PERSONAL_COLOR;
            }
          }
        }

        return PERSONAL_COLOR;
      },
      getColorForRep: (host) => {
        if (!host) return PERSONAL_COLOR;
        const keys = [host.id, host.userId, host.email, host.name, host.username]
          .filter(Boolean)
          .map((k) => String(k).trim().toLowerCase());
        for (const key of keys) {
          if (map.has(key)) return map.get(key);
        }
        return PERSONAL_COLOR;
      },
      personalColor: PERSONAL_COLOR,
      googleColor: EXTERNAL_COLOR,
      externalColor: EXTERNAL_COLOR,
    };
  }, [otherTeamHosts]);

  // 1. Dynamic Team Meeting Hosts Filters
  const [hostFilters, setHostFilters] = useState(() => {
    const map = {};
    (hosts || []).forEach((h) => {
      map[h.id] = true;
    });
    return map;
  });

  useEffect(() => {
    if (hosts && hosts.length > 0) {
      setHostFilters((prev) => {
        const next = { ...prev };
        hosts.forEach((h) => {
          if (next[h.id] === undefined) next[h.id] = true;
        });
        return next;
      });
    }
  }, [hosts]);

  // 2. Dynamic Meeting Types Filters
  const [typeFilters, setTypeFilters] = useState(() => {
    const map = {};
    (meetingTypes || []).forEach((t) => {
      map[t.id || t.slug] = true;
    });
    return map;
  });

  useEffect(() => {
    if (meetingTypes && meetingTypes.length > 0) {
      setTypeFilters((prev) => {
        const next = { ...prev };
        meetingTypes.forEach((t) => {
          const key = t.id || t.slug;
          if (next[key] === undefined) next[key] = true;
        });
        return next;
      });
    }
  }, [meetingTypes]);

  const [dayPopoverData, setDayPopoverData] = useState(null);

  // Month navigation helpers
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleToday = () => {
    const now = new Date();
    setCurrentDate(now);
    setSelectedDate(now);
  };

  const toggleHostFilter = (hostId) => {
    setHostFilters((prev) => ({ ...prev, [hostId]: !prev[hostId] }));
  };

  const toggleTypeFilter = (typeKey) => {
    setTypeFilters((prev) => ({ ...prev, [typeKey]: !prev[typeKey] }));
  };

  // Query live Google Calendar busy slots for the active month view (strictly scoped to the current user's connected calendar)
  useEffect(() => {
    let isMounted = true;
    const myId = currentUser?.id || currentUser?.sub || currentUser?.user_id || '';

    if (!showExternalBusy || !workspaceId) {
      setLiveBusySlots([]);
      return;
    }

    // Buffer 14 days before and after month to cover full 42-day visible grid
    const startWindow = new Date(year, month - 1, 15, 0, 0, 0);
    const endWindow = new Date(year, month + 1, 15, 23, 59, 59);

    if (typeof calendarApi?.getExternalBusySlots === 'function') {
      calendarApi
        .getExternalBusySlots(workspaceId, startWindow.toISOString(), endWindow.toISOString(), myId)
        ?.then((slots) => {
          if (isMounted && Array.isArray(slots)) {
            setLiveBusySlots(slots);
          }
        })
        ?.catch((err) => {
          console.warn('[MeetingCalendarView] Failed to fetch external busy slots:', err?.message);
        });
    }

    return () => {
      isMounted = false;
    };
  }, [workspaceId, year, month, showExternalBusy, currentUser]);

  // Convert live database meetings into unified calendar event objects
  const allEvents = useMemo(() => {
    const list = [];

    // 1. Live meetings from database / props
    meetings.forEach((m) => {
      if (!m.startTime) return;
      const d = new Date(m.startTime);
      const dateStr = formatDateKey(d);
      const timeStr = formatEventTime(d);

      let endStr = '';
      if (m.endTime) {
        endStr = formatEventTime(m.endTime);
      }

      let category = 'enterprise';
      let meetingTypeKey = m.meetingTypeId || 'product-demo';

      const typeNameLower = (m.meetingTypeName || m.title || '').toLowerCase();
      if (
        m.meetingTypeId === 'mt-2' ||
        typeNameLower.includes('discovery') ||
        typeNameLower.includes('intro')
      ) {
        meetingTypeKey = m.meetingTypeId || 'quick-intro';
        category = 'growth';
      } else if (
        m.meetingTypeId === 'mt-3' ||
        typeNameLower.includes('security') ||
        typeNameLower.includes('tech') ||
        typeNameLower.includes('architecture')
      ) {
        meetingTypeKey = m.meetingTypeId || 'tech-review';
        category = 'enterprise';
      } else if (
        m.meetingTypeId === 'mt-4' ||
        typeNameLower.includes('enterprise') ||
        typeNameLower.includes('procurement') ||
        typeNameLower.includes('quote')
      ) {
        meetingTypeKey = m.meetingTypeId || 'enterprise-quote';
        category = 'enterprise';
      }

      const startHour = d.getHours();
      const hostColor = hostColorMap.getColorForHost(
        m.hostId || m.host_id,
        m.hostName || m.host_name,
        m.hostEmail || m.host_email,
        m
      );

      list.push({
        id: m.id,
        rawMeeting: m,
        title:
          m.title ||
          `${m.meetingTypeName || 'Meeting'}: ${m.guestName || m.guestCompany || 'Guest'}`,
        dateStr,
        date: d,
        timeStr,
        timeRange: endStr ? `${timeStr} - ${endStr}` : timeStr,
        startHour,
        durationMinutes: m.durationMinutes || 30,
        hostId: m.hostId || 'usr-1',
        hostName: m.hostName || 'Workspace Host',
        hostColor,
        guestName: m.guestName || '',
        guestCompany: m.guestCompany && m.guestCompany !== 'Prospect Org' ? m.guestCompany : '',
        guestTitle: m.guestTitle,
        status: getEffectiveMeetingStatus(m),
        isPast: isMeetingPast(m),
        canJoin: canJoinMeeting(m),
        countdownText: formatMeetingCountdown(getMsUntilMeetingAccessOpen(m)),
        category,
        meetingType: meetingTypeKey,
        dealEstimate: m.dealEstimate || '$35,000 ARR',
        conferencingUrl: m.conferencingUrl || 'https://meet.google.com/lead-demo',
        description:
          m.preMeetingBrief?.accountSummary ||
          m.guestNotes ||
          'Lead qualification session covering outbound pipeline requirements and tenant isolation.',
        attendeeCount: 4,
      });
    });

    // 2. Real connected calendar busy slots (if enabled & available from Google/Outlook)
    if (showExternalBusy && liveBusySlots && liveBusySlots.length > 0) {
      // Build robust matchers for all internal platform meetings to prevent duplicates
      const internalGoogleIds = new Set();
      const internalMeetingsMeta = [];

      meetings.forEach((m) => {
        const gId = m.googleEventId || m.google_event_id || m.rawMeeting?.google_event_id;
        if (gId) internalGoogleIds.add(String(gId).trim());

        const mStart =
          m.date instanceof Date ? m.date : new Date(m.startTime || m.start_time || m.start);
        const mEnd =
          m.end instanceof Date
            ? m.end
            : new Date(
                m.endTime ||
                  m.end_time ||
                  m.end ||
                  mStart.getTime() + (m.durationMinutes || 30) * 60000
              );

        if (!isNaN(mStart.getTime())) {
          internalMeetingsMeta.push({
            id: m.id,
            googleEventId: gId ? String(gId).trim() : null,
            dateStr: formatDateKey(mStart),
            timeStr: formatEventTime(mStart),
            startMs: mStart.getTime(),
            endMs: isNaN(mEnd.getTime())
              ? mStart.getTime() + (m.durationMinutes || 30) * 60000
              : mEnd.getTime(),
            title: (m.title || '').trim().toLowerCase(),
            conferencingUrl: (m.conferencingUrl || '').trim().toLowerCase(),
          });
        }
      });

      liveBusySlots.forEach((slot) => {
        if (!slot.start) return;

        // 1. Direct ID match
        if (slot.id && Array.from(internalGoogleIds).some((gid) => slot.id.includes(gid))) {
          return;
        }

        const isDateOnly =
          slot.isAllDay ||
          (typeof slot.start === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(slot.start.trim()));
        const dStart = isDateOnly
          ? new Date(
              `${typeof slot.start === 'string' ? slot.start.trim() : formatDateKey(slot.start)}T09:00:00`
            )
          : new Date(slot.start);
        const dEnd = slot.end
          ? isDateOnly
            ? new Date(
                `${typeof slot.end === 'string' ? slot.end.trim() : formatDateKey(slot.end)}T10:00:00`
              )
            : new Date(slot.end)
          : dStart;

        const slotStartMs = dStart.getTime();
        const slotEndMs = dEnd.getTime();
        const slotDateStr = formatDateKey(dStart);
        const slotTimeStr = isDateOnly ? 'All Day' : formatEventTime(dStart);
        const slotTitleLower = (slot.title || '').trim().toLowerCase();
        const slotLocationLower = (slot.location || '').trim().toLowerCase();
        const slotLinkLower = (slot.htmlLink || '').trim().toLowerCase();

        // 2. Intelligent overlap deduplication with internal Lead Huntrix meetings
        const isDuplicateOfInternal = internalMeetingsMeta.some((im) => {
          // Direct ID inclusion
          if (im.googleEventId && slot.id && slot.id.includes(im.googleEventId)) {
            return true;
          }

          // Start time alignment within a 2-minute margin OR exact matching date + time string
          const startDiff = Math.abs(slotStartMs - im.startMs);
          const sameStartTime =
            startDiff <= 2 * 60 * 1000 ||
            (slotDateStr === im.dateStr && slotTimeStr === im.timeStr);

          if (sameStartTime) {
            // Check if slot has a generic title (e.g. "Google Calendar Event", "Busy", "Google...")
            const isGenericCalendarTitle =
              !slotTitleLower ||
              slotTitleLower.includes('google') ||
              slotTitleLower.includes('outlook') ||
              slotTitleLower.includes('teams') ||
              slotTitleLower === 'busy' ||
              slotTitleLower === 'calendar event';

            if (isGenericCalendarTitle) return true;

            // Direct title similarity (e.g. "Product Demo" vs "Product Demo")
            if (
              im.title &&
              (slotTitleLower.includes(im.title) || im.title.includes(slotTitleLower))
            ) {
              return true;
            }

            // Matching conferencing link (e.g. meet.google.com/xyz)
            if (
              im.conferencingUrl &&
              (slotLocationLower.includes(im.conferencingUrl) ||
                slotLinkLower.includes(im.conferencingUrl))
            ) {
              return true;
            }

            // Exact end time alignment as well
            const endDiff = Math.abs(slotEndMs - im.endMs);
            if (endDiff <= 2 * 60 * 1000) {
              return true;
            }
          }

          return false;
        });

        if (isDuplicateOfInternal) {
          return;
        }

        const dStr = formatDateKey(slot.start);
        const startTimeStr = isDateOnly ? 'All Day' : formatEventTime(dStart);
        const endTimeStr = isDateOnly ? '' : formatEventTime(dEnd);
        const durMinutes = isDateOnly
          ? 60
          : Math.max(15, Math.round((dEnd.getTime() - dStart.getTime()) / 60000));
        const hostColor =
          slot.color && slot.color !== '#0078d4'
            ? slot.color
            : hostColorMap.externalColor || hostColorMap.googleColor;

        // 3. Self-deduplication against already processed external items in list
        const existingExternalIdx = list.findIndex((item) => {
          if (!item.isExternal) return false;
          const startDiff = Math.abs(dStart.getTime() - item.date.getTime());
          return (
            startDiff <= 2 * 60 * 1000 || (item.dateStr === dStr && item.timeStr === startTimeStr)
          );
        });

        if (existingExternalIdx >= 0) {
          const existingItem = list[existingExternalIdx];
          const existingIsGeneric =
            !existingItem.title ||
            existingItem.title.toLowerCase().includes('google calendar event') ||
            existingItem.title.toLowerCase().includes('outlook calendar event') ||
            existingItem.title.toLowerCase() === 'busy';
          const currentIsGeneric =
            !slot.title ||
            slot.title.toLowerCase().includes('google calendar event') ||
            slot.title.toLowerCase().includes('outlook calendar event') ||
            slot.title.toLowerCase() === 'busy';

          // If existing is generic placeholder but current slot has real title, replace it
          if (existingIsGeneric && !currentIsGeneric) {
            list[existingExternalIdx] = {
              ...existingItem,
              id: slot.id,
              title: slot.title,
              location: slot.location || existingItem.location,
              htmlLink: slot.htmlLink || existingItem.htmlLink,
            };
          }
          // Discard redundant duplicate slot
          return;
        }

        list.push({
          id: slot.id,
          isExternal: true,
          isAllDay: isDateOnly,
          provider: slot.provider || 'google',
          meeting: null,
          title:
            slot.title ||
            (slot.provider === 'microsoft' || slot.provider === 'outlook'
              ? 'Microsoft Calendar Event'
              : 'Google Calendar Event'),
          dateStr: dStr,
          date: dStart,
          timeStr: startTimeStr,
          timeRange: endTimeStr ? `${startTimeStr} - ${endTimeStr}` : startTimeStr,
          startHour: isDateOnly ? 9 : dStart.getHours(),
          durationMinutes: durMinutes,
          hostId: slot.hostId || 'usr-1',
          hostName:
            slot.hostName ||
            (slot.provider === 'microsoft' || slot.provider === 'outlook'
              ? 'Microsoft Calendar'
              : 'Google Calendar'),
          accountEmail: slot.accountEmail || slot.account || connectedGoogleEmail,
          hostColor,
          guestName: '',
          guestCompany: '',
          status: 'external_busy',
          category: 'external',
          location: slot.location || '',
          htmlLink: slot.htmlLink || '',
        });
      });
    }

    // Sort all events chronologically by start time, and then by earliest end time
    list.sort(compareCalendarEvents);

    return list;
  }, [meetings, showExternalBusy, liveBusySlots, connectedGoogleEmail]);

  // Filter events based on active Host and Meeting Type filters
  const visibleEvents = useMemo(() => {
    return allEvents.filter((ev) => {
      // External Google/Outlook events bypass meeting type and team rep host filters
      if (ev.isExternal) return true;
      // 1. Host filter
      if (ev.hostId && hostFilters[ev.hostId] === false) return false;
      // 2. Meeting Type filter
      if (ev.meetingType && typeFilters[ev.meetingType] === false) return false;
      return true;
    });
  }, [allEvents, hostFilters, typeFilters]);

  // Build calendar matrix (42 cells: 6 rows x 7 days)
  const calendarMatrix = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday...
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const matrix = [];
    const todayStr = formatDateKey(new Date());
    const selectedStr = formatDateKey(selectedDate);

    // 1. Previous month trailing days
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const dayNum = daysInPrevMonth - i;
      const prevDate = new Date(year, month - 1, dayNum);
      const dateStr = formatDateKey(prevDate);
      const dayEvents = visibleEvents
        .filter((e) => e.dateStr === dateStr && e.status !== 'cancelled')
        .sort(compareCalendarEvents);
      matrix.push({
        date: prevDate,
        dateStr,
        dayNum,
        isCurrentMonth: false,
        isToday: dateStr === todayStr,
        isSelected: dateStr === selectedStr,
        events: dayEvents,
      });
    }

    // 2. Current month days
    for (let dayNum = 1; dayNum <= daysInMonth; dayNum++) {
      const thisDate = new Date(year, month, dayNum);
      const dateStr = formatDateKey(thisDate);
      const dayEvents = visibleEvents
        .filter((e) => e.dateStr === dateStr && e.status !== 'cancelled')
        .sort(compareCalendarEvents);
      matrix.push({
        date: thisDate,
        dateStr,
        dayNum,
        isCurrentMonth: true,
        isToday: dateStr === todayStr,
        isSelected: dateStr === selectedStr,
        events: dayEvents,
      });
    }

    // 3. Next month leading days
    const remainingCells = 42 - matrix.length;
    for (let dayNum = 1; dayNum <= remainingCells; dayNum++) {
      const nextDate = new Date(year, month + 1, dayNum);
      const dateStr = formatDateKey(nextDate);
      const dayEvents = visibleEvents
        .filter((e) => e.dateStr === dateStr && e.status !== 'cancelled')
        .sort(compareCalendarEvents);
      matrix.push({
        date: nextDate,
        dateStr,
        dayNum,
        isCurrentMonth: false,
        isToday: dateStr === todayStr,
        isSelected: dateStr === selectedStr,
        events: dayEvents,
      });
    }

    return matrix;
  }, [year, month, visibleEvents, selectedDate]);

  // Formatted date string for Month Header
  const monthNameFormatted = currentDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  // Formatted date string for Day Timeline Header (e.g. "September 29, 2026")
  const selectedDayFormatted = selectedDate.toLocaleDateString('en-US', {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  });

  // Day of week + day number for the Day Timeline inner bar (e.g. "SUN, 09" or "TUE, 01")
  const selectedDayWeekHeader = `${selectedDate
    .toLocaleDateString('en-US', { weekday: 'short' })
    .toUpperCase()}, ${String(selectedDate.getDate()).padStart(2, '0')}`;

  // Events belonging to the currently selected date in Day View
  const selectedDateStr = formatDateKey(selectedDate);
  const selectedDayEvents = useMemo(() => {
    return visibleEvents.filter((ev) => ev.dateStr === selectedDateStr).sort(compareCalendarEvents);
  }, [visibleEvents, selectedDateStr]);

  // When clicking a month cell: select date and switch to Day Timeline View
  const handleCellClick = (cell) => {
    setSelectedDate(cell.date);
    if (!cell.isCurrentMonth) {
      setCurrentDate(new Date(cell.date.getFullYear(), cell.date.getMonth(), 1));
    }
    setActiveViewMode('day');
  };

  const handleMiniDayClick = (cell) => {
    setSelectedDate(cell.date);
    if (!cell.isCurrentMonth) {
      setCurrentDate(new Date(cell.date.getFullYear(), cell.date.getMonth(), 1));
    }
  };

  // When clicking a specific session / event pill: open details without changing selected date or view mode
  const handleEventClick = (e, ev) => {
    e.stopPropagation();
    if (ev?.isExternal) {
      setSelectedExternalEvent(ev);
      return;
    }
    if (onSelectMeeting && ev && ev.rawMeeting) {
      onSelectMeeting(ev.rawMeeting);
    }
  };

  const handleOpenAddModal = (dateStr) => {
    if (!canCreateMeeting) return;
    if (onOpenScheduleModal) {
      onOpenScheduleModal(dateStr);
    }
  };

  return (
    <div className="cal-dashboard-container">
      {/* ── B2B LEAD PIPELINE LEFT SIDEBAR ── */}
      <aside className="cal-sidebar">
        {/* Top: Mini Month Picker */}
        <div className="cal-mini-picker-section">
          <div className="cal-mini-header">
            <span className="cal-mini-month-title">{monthNameFormatted}</span>
            <div className="cal-mini-nav-btns">
              <button
                type="button"
                className="cal-mini-nav-btn"
                onClick={handlePrevMonth}
                title="Previous Month"
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <button
                type="button"
                className="cal-mini-nav-btn"
                onClick={handleNextMonth}
                title="Next Month"
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>

          <div className="cal-mini-weekdays-row">
            {MINI_WEEKDAYS.map((wd, i) => (
              <span key={i} className="cal-mini-weekday-cell">
                {wd}
              </span>
            ))}
          </div>

          <div className="cal-mini-days-grid">
            {calendarMatrix.map((cell, idx) => {
              const hasEvents = cell.events.length > 0;
              const hasEnterprise = cell.events.some((e) => e.category === 'enterprise');
              const hasMidMarket = cell.events.some((e) => e.category === 'midmarket');
              const hasGrowth = cell.events.some((e) => e.category === 'growth');

              return (
                <div
                  key={idx}
                  className={`cal-mini-day-cell ${!cell.isCurrentMonth ? 'dimmed' : ''} ${
                    cell.isSelected ? 'selected' : ''
                  } ${cell.isToday ? 'today' : ''}`}
                  onClick={() => handleMiniDayClick(cell)}
                >
                  <span className="cal-mini-day-num">{cell.dayNum}</span>
                  {hasEvents && (
                    <div className="cal-mini-dots-row">
                      {hasEnterprise && <span className="cal-mini-dot dot-blue" />}
                      {hasMidMarket && <span className="cal-mini-dot dot-orange" />}
                      {hasGrowth && <span className="cal-mini-dot dot-red" />}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Section 1 Header: MEETING HOSTS & Team Reps (Only visible for Manager, Admin, Owner) */}
        {showTeamHostsSection && (
          <>
            <div className="cal-sidebar-section-header">
              <span className="cal-sidebar-section-label">MEETING HOSTS</span>
              {canCreateMeeting && (
                <button
                  type="button"
                  className="cal-sidebar-quick-add-btn"
                  title="Schedule New Lead Meeting"
                  onClick={() => handleOpenAddModal(formatDateKey(selectedDate))}
                >
                  <i className="fas fa-plus"></i>
                </button>
              )}
            </div>

            {/* Accordion 1: Assigned Team Hosts */}
            <div className="cal-sidebar-group-block">
              <div
                className="cal-sidebar-group-header"
                onClick={() => setIsHostsOpen(!isHostsOpen)}
              >
                <span>Team Reps</span>
                <i className={`fas fa-chevron-${isHostsOpen ? 'up' : 'down'}`}></i>
              </div>

              {isHostsOpen && (
                <div className="cal-sidebar-checkbox-list">
                  {otherTeamHosts && otherTeamHosts.length > 0 ? (
                    otherTeamHosts.map((host) => (
                      <label key={host.id} className="cal-checkbox-item">
                        <input
                          type="checkbox"
                          checked={hostFilters[host.id] !== false}
                          onChange={() => toggleHostFilter(host.id)}
                        />
                        <span className="cal-custom-box"></span>
                        <span
                          className="cal-host-dot"
                          style={{
                            backgroundColor: hostColorMap.getColorForRep(host),
                          }}
                        ></span>
                        <span className="cal-item-label">{host.name || host.username}</span>
                      </label>
                    ))
                  ) : (
                    <div style={{ padding: '8px 12px', fontSize: '13px', color: '#64748b' }}>
                      No other team reps in workspace
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Accordion 2: Meeting Types */}
        <div className="cal-sidebar-group-block">
          <div className="cal-sidebar-group-header" onClick={() => setIsTypesOpen(!isTypesOpen)}>
            <span>Meeting Types</span>
            <i className={`fas fa-chevron-${isTypesOpen ? 'up' : 'down'}`}></i>
          </div>

          {isTypesOpen && (
            <div className="cal-sidebar-checkbox-list">
              {meetingTypes && meetingTypes.length > 0 ? (
                meetingTypes.map((type) => {
                  const typeKey = type.id || type.slug;
                  return (
                    <label key={typeKey} className="cal-checkbox-item">
                      <input
                        type="checkbox"
                        checked={typeFilters[typeKey] !== false}
                        onChange={() => toggleTypeFilter(typeKey)}
                      />
                      <span className="cal-custom-box"></span>
                      <span className="cal-item-label">{type.title}</span>
                    </label>
                  );
                })
              ) : (
                <div style={{ padding: '8px 12px', fontSize: '13px', color: '#64748b' }}>
                  No meeting types configured
                </div>
              )}
            </div>
          )}
        </div>

        {/* Schedule Color Identifier Note / Legend */}
        <div className="cal-sidebar-legend-card">
          <div className="cal-sidebar-legend-header">
            <span className="cal-sidebar-section-label">SCHEDULE LEGEND</span>
          </div>
          <div className="cal-sidebar-legend-list">
            <div className="cal-legend-item">
              <span
                className={`cal-legend-dot dot-google ${
                  connectedCalendarProvider === 'microsoft' ? 'dot-microsoft' : ''
                }`.trim()}
                style={{ backgroundColor: hostColorMap.externalColor || hostColorMap.googleColor }}
              ></span>
              <div className="cal-legend-text">
                <span className="cal-legend-title">
                  {connectedCalendarProvider === 'microsoft'
                    ? 'Microsoft Calendar Task'
                    : 'Google Calendar Task'}
                </span>
                <span className="cal-legend-desc">Synced external busy event</span>
              </div>
            </div>
            <div className="cal-legend-item">
              <span
                className="cal-legend-dot dot-personal"
                style={{ backgroundColor: hostColorMap.personalColor }}
              ></span>
              <div className="cal-legend-text">
                <span className="cal-legend-title">Personal Schedule Task</span>
                <span className="cal-legend-desc">Internal scheduled meeting / task</span>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* ── MAIN CALENDAR AREA ── */}
      <main className="cal-main-content">
        {/* Top Action Bar */}
        <div className="cal-main-header">
          <h2 className="cal-main-month-title">
            {activeViewMode === 'day' ? selectedDayFormatted : monthNameFormatted}
          </h2>

          <div className="cal-main-actions-row">
            <button type="button" className="cal-btn-today" onClick={handleToday}>
              Today
            </button>

            <button
              type="button"
              className={`cal-btn-toggle-busy ${hasConnectedCalendar && showExternalBusy ? 'active' : ''}`}
              onClick={() => {
                if (hasConnectedCalendar) {
                  setShowExternalBusy(!showExternalBusy);
                }
              }}
              disabled={!hasConnectedCalendar}
              title={
                hasConnectedCalendar
                  ? 'Toggle Google Calendar & Outlook external busy conflicts'
                  : 'No Google or Outlook calendar connected for this account. Connect in Calendar Settings.'
              }
            >
              <i className="fas fa-calendar-check"></i>
              <span>
                Calendar Sync:{' '}
                {!hasConnectedCalendar ? 'Disconnected' : showExternalBusy ? 'Active' : 'Off'}
              </span>
            </button>

            <button type="button" className="cal-btn-icon-filter" title="Filter Calendar View">
              <i className="fas fa-filter"></i>
            </button>

            {/* View Switchers: Month Grid vs Day Timeline */}
            <div className="cal-view-switcher">
              <button
                type="button"
                className={`cal-view-btn ${activeViewMode === 'month' ? 'active' : ''}`}
                onClick={() => setActiveViewMode('month')}
                title="Month Grid View"
              >
                <i className="fas fa-th-large"></i>
              </button>
              <button
                type="button"
                className={`cal-view-btn ${activeViewMode === 'day' ? 'active' : ''}`}
                onClick={() => setActiveViewMode('day')}
                title="Day Timeline View"
              >
                <i className="fas fa-bars"></i>
              </button>
            </div>
          </div>
        </div>

        {/* ── VIEW 1: MONTH GRID VIEW ── */}
        {activeViewMode === 'month' && (
          <div className="cal-month-grid-container">
            {/* Weekday Column Headers */}
            <div className="cal-month-weekdays-row">
              {WEEKDAYS.map((day, idx) => (
                <div key={idx} className="cal-month-weekday-header">
                  {day}
                </div>
              ))}
            </div>

            {/* 6x7 Month Day Cells Grid */}
            <div className="cal-month-cells-grid">
              {calendarMatrix.map((cell, idx) => {
                const isSelectedCell = cell.isSelected;
                const isTodayCell = cell.isToday;

                return (
                  <div
                    key={idx}
                    className={`cal-month-cell ${!cell.isCurrentMonth ? 'not-current-month' : ''} ${
                      isSelectedCell ? 'is-selected-cell' : ''
                    }`}
                    onClick={() => handleCellClick(cell)}
                    title={`Select ${cell.dateStr}`}
                  >
                    <div className="cal-cell-header">
                      {isTodayCell ? (
                        <span className="cal-cell-badge today-badge" title="Today">
                          {cell.dayNum}
                        </span>
                      ) : isSelectedCell ? (
                        <span className="cal-cell-badge selected-badge" title="Selected Date">
                          {cell.dayNum}
                        </span>
                      ) : (
                        <span className="cal-cell-num">{cell.dayNum}</span>
                      )}

                      <button
                        type="button"
                        className="cal-cell-quick-add"
                        title="Schedule meeting on this date"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenAddModal(cell.dateStr);
                        }}
                      >
                        <i className="fas fa-plus"></i>
                      </button>
                    </div>

                    {/* Events List inside Day Cell */}
                    <div className="cal-cell-events-flow">
                      {cell.events.slice(0, 3).map((ev) => (
                        <div
                          key={ev.id}
                          className={`cal-event-pill ${
                            ev.isExternal
                              ? `external-busy ${ev.provider}`
                              : `category-${ev.category || 'enterprise'}`
                          }`}
                          onClick={(e) => handleEventClick(e, ev)}
                          title={`Click session to view details: ${ev.title} (${ev.timeStr})`}
                        >
                          <span
                            className="cal-pill-indicator"
                            style={{ backgroundColor: ev.hostColor }}
                          ></span>
                          <span className="cal-pill-time">{ev.timeStr}</span>
                          <span className="cal-pill-title">{ev.title}</span>
                        </div>
                      ))}

                      {cell.events.length > 3 && (
                        <div
                          className="cal-event-more-badge"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDayPopoverData(cell);
                          }}
                        >
                          +{cell.events.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── VIEW 2: DAY TIMELINE VIEW ── */}
        {activeViewMode === 'day' && (
          <div className="day-timeline-wrapper">
            {/* Top Sub-Bar: e.g. "SUN, 09" on left and "GMT+08" on right */}
            <div className="day-timeline-subbar">
              <div className="day-subbar-date-label">
                <button
                  type="button"
                  className="day-back-to-month-btn"
                  onClick={() => setActiveViewMode('month')}
                  title="Back to Month View"
                >
                  <i className="fas fa-arrow-left" />
                </button>
                <span>{selectedDayWeekHeader}</span>
              </div>
              <span className="day-subbar-tz-label">GMT+08</span>
            </div>

            {/* Hourly Schedule Timeline Canvas */}
            <div className="day-timeline-canvas">
              {TIMELINE_HOURS.map(({ hour, label }) => {
                const hourEvents = selectedDayEvents.filter((e) => e.startHour === hour);

                return (
                  <div key={hour} className="day-timeline-hour-row">
                    <div className="day-hour-label-col">
                      <span>{label}</span>
                    </div>

                    <div
                      className="day-hour-slot-col"
                      onClick={() =>
                        handleOpenAddModal(
                          `${selectedDateStr}T${String(hour).padStart(2, '0')}:00:00`
                        )
                      }
                    >
                      {hourEvents.map((ev) =>
                        ev.isExternal ? (
                          <div
                            key={ev.id}
                            className={`day-timeline-event-card external-busy ${ev.provider}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedExternalEvent(ev);
                            }}
                            style={{ cursor: 'pointer' }}
                            title={`Click to view details for ${ev.title}`}
                          >
                            <div className="day-event-card-inner">
                              <div className="day-event-card-top-row">
                                <div className="day-event-card-title-wrap">
                                  <strong className="day-event-card-title">{ev.title}</strong>
                                  <span className="cal-status-badge google-cal">
                                    {ev.provider === 'microsoft' || ev.provider === 'outlook'
                                      ? 'Microsoft Calendar'
                                      : 'Google Calendar'}
                                  </span>
                                </div>
                                <span className="day-event-card-time-range">{ev.timeRange}</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div
                            key={ev.id}
                            className={`day-timeline-event-card category-${ev.category || 'enterprise'} ${
                              ev.status === 'cancelled' ? 'cancelled' : ''
                            }`}
                            style={{ borderLeftColor: ev.hostColor }}
                            onClick={(e) => handleEventClick(e, ev)}
                          >
                            <div className="day-event-card-inner">
                              {/* Line 1: Title + Time Range */}
                              <div className="day-event-card-top-row">
                                <div className="day-event-card-title-wrap">
                                  <strong className="day-event-card-title">{ev.title}</strong>
                                  {ev.status === 'cancelled' && (
                                    <span className="cal-status-badge cancelled">Cancelled</span>
                                  )}
                                  {ev.status === 'in_progress' && (
                                    <span className="cal-status-badge in_progress">
                                      <span className="live-pulse-dot"></span>
                                      In Progress
                                    </span>
                                  )}
                                  {(ev.status === 'completed' || ev.isPast) &&
                                    ev.status !== 'cancelled' &&
                                    ev.status !== 'in_progress' && (
                                      <span className="cal-status-badge completed">Completed</span>
                                    )}
                                </div>
                                <span className="day-event-card-time-range">{ev.timeRange}</span>
                              </div>

                              {/* Line 2: Video Conferencing URL Link */}
                              {ev.conferencingUrl && ev.status !== 'cancelled' && (
                                <div className="day-event-card-mid-row">
                                  {ev.status === 'completed' || ev.isPast ? (
                                    <span
                                      className="day-event-card-link is-ended unclickable"
                                      onClick={(e) => e.stopPropagation()}
                                      onCopy={(e) => e.preventDefault()}
                                      title="Meeting Completed (Link Inactive)"
                                    >
                                      <i className="fas fa-link" />
                                      <span>{ev.conferencingUrl}</span>
                                    </span>
                                  ) : (
                                    <a
                                      href={ev.conferencingUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="day-event-card-link"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <i className="fas fa-link" />
                                      <span>{ev.conferencingUrl}</span>
                                    </a>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* Day Events Popover Modal when "+X more" is clicked */}
      {dayPopoverData && (
        <div className="cal-popover-overlay" onClick={() => setDayPopoverData(null)}>
          <div className="cal-popover-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cal-popover-header">
              <h3>
                Lead Meetings for{' '}
                {dayPopoverData.date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </h3>
              <button
                type="button"
                className="cal-popover-close"
                onClick={() => setDayPopoverData(null)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="cal-popover-body">
              {dayPopoverData.events.map((ev) => {
                const matchedHost = (hosts || []).find(
                  (h) =>
                    (ev.hostId && (h.id === ev.hostId || h.userId === ev.hostId)) ||
                    (ev.rawMeeting?.hostEmail &&
                      h.email &&
                      h.email.toLowerCase() === ev.rawMeeting.hostEmail.toLowerCase())
                );
                const assignedStaffName =
                  matchedHost?.name ||
                  (ev.hostName && ev.hostName !== 'Workspace Host' ? ev.hostName : '') ||
                  (ev.rawMeeting?.hostName && ev.rawMeeting.hostName !== 'Unassigned'
                    ? ev.rawMeeting.hostName
                    : '');

                return (
                  <div
                    key={ev.id}
                    className={`cal-event-pill expanded ${
                      ev.isExternal
                        ? `external-busy ${ev.provider}`
                        : `category-${ev.category || 'enterprise'}`
                    }`}
                    onClick={(e) => {
                      setDayPopoverData(null);
                      handleEventClick(e, ev);
                    }}
                  >
                    <span
                      className="cal-pill-indicator"
                      style={{ backgroundColor: ev.hostColor }}
                    ></span>
                    <span className="cal-pill-time">{ev.timeStr}</span>
                    <span className="cal-pill-title">{ev.title}</span>
                    {assignedStaffName ? (
                      <span className="cal-pill-host">{assignedStaffName}</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* External Calendar Event Details Modal */}
      {selectedExternalEvent && (
        <div className="meetings-modal-overlay" onClick={() => setSelectedExternalEvent(null)}>
          <div
            className="meetings-modal-container modern-cal ext-cal-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Minimal Bar */}
            <div className="cal-modal-topbar">
              <div className="cal-modal-status-wrap">
                <span
                  className={`meeting-modal-status-badge external ${selectedExternalEvent.provider}`}
                >
                  <span className="status-dot"></span>
                  {selectedExternalEvent.provider === 'microsoft' ||
                  selectedExternalEvent.provider === 'outlook'
                    ? 'Microsoft 365 Outlook'
                    : 'Google Calendar'}
                </span>
                <span className="cal-modal-type-chip">Personal Schedule</span>
              </div>
              <button
                type="button"
                className="meetings-modal-close"
                onClick={() => setSelectedExternalEvent(null)}
                aria-label="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Header Title & Date Bar */}
            <div className="cal-modal-header">
              <h2 className="meetings-modal-title">{selectedExternalEvent.title}</h2>

              {/* Schedule Hero Card */}
              <div className="cal-schedule-hero-container">
                <div className="cal-schedule-hero-card cal-schedule-entry is-current">
                  <div className={`cal-date-badge active ${selectedExternalEvent.provider}`}>
                    <span className="cal-date-badge-month">
                      {selectedExternalEvent.date instanceof Date
                        ? selectedExternalEvent.date
                            .toLocaleDateString('en-US', { month: 'short' })
                            .toUpperCase()
                        : 'CAL'}
                    </span>
                    <span className="cal-date-badge-day">
                      {selectedExternalEvent.date instanceof Date
                        ? String(selectedExternalEvent.date.getDate()).padStart(2, '0')
                        : '--'}
                    </span>
                  </div>
                  <div className="cal-schedule-hero-info">
                    <div className="cal-schedule-hero-date-row">
                      <span className="cal-schedule-hero-date">
                        {selectedExternalEvent.date instanceof Date
                          ? selectedExternalEvent.date.toLocaleDateString('en-US', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            })
                          : selectedExternalEvent.dateStr}
                      </span>
                      <span className="cal-schedule-duration-pill">
                        {selectedExternalEvent.durationMinutes || 60} mins
                      </span>
                    </div>
                    <div className="cal-schedule-hero-time-row">
                      <i className="far fa-clock"></i>
                      <span>{selectedExternalEvent.timeRange}</span>
                      <span className="cal-schedule-tz-tag">GMT+8</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* External Calendar Information Notice Box */}
              <div className="ext-event-notice-box">
                <div className="ext-event-notice-icon">
                  <i className="fas fa-calendar-alt"></i>
                </div>
                <div className="ext-event-notice-body">
                  <span className="ext-event-notice-title">Personal Calendar Task / Busy Slot</span>
                  <p className="ext-event-notice-desc">
                    This is an external task synced directly from your personal{' '}
                    <strong>
                      {selectedExternalEvent.provider === 'microsoft' ||
                      selectedExternalEvent.provider === 'outlook'
                        ? 'Microsoft Outlook'
                        : 'Google Calendar'}
                    </strong>
                    . It automatically reserves your time and prevents overlapping CRM meeting
                    bookings.
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Body / Details */}
            <div className="meetings-modal-content">
              <div className="cal-details-pane">
                {/* Source & Account Information */}
                <div className="cal-section">
                  <h4 className="cal-section-label">Source & Host Details</h4>
                  <div className="cal-participants-list">
                    <div className="cal-participant-row">
                      <div
                        className="cal-avatar guest"
                        style={{
                          background: '#fef2f2',
                          color: '#ea4335',
                          borderColor: '#fecaca',
                        }}
                      >
                        <i
                          className={
                            selectedExternalEvent.provider === 'microsoft' ||
                            selectedExternalEvent.provider === 'outlook'
                              ? 'fab fa-microsoft'
                              : 'fab fa-google'
                          }
                        ></i>
                      </div>
                      <div className="cal-participant-info">
                        <div className="cal-participant-name-line">
                          <span className="cal-name">
                            {selectedExternalEvent.provider === 'microsoft' ||
                            selectedExternalEvent.provider === 'outlook'
                              ? 'Microsoft 365 Outlook'
                              : 'Google Calendar'}
                          </span>
                          <span className="cal-role-tag guest">Personal Schedule</span>
                        </div>
                        <div className="cal-participant-sub">
                          <span>
                            Linked{' '}
                            {selectedExternalEvent.provider === 'microsoft' ||
                            selectedExternalEvent.provider === 'outlook'
                              ? 'Outlook'
                              : 'Google'}{' '}
                            Account:{' '}
                            <strong style={{ color: '#0f172a', fontWeight: 600 }}>
                              {selectedExternalEvent.accountEmail || connectedGoogleEmail}
                            </strong>
                          </span>
                        </div>
                        {selectedExternalEvent.hostName &&
                          selectedExternalEvent.hostName !== 'Google Calendar' &&
                          selectedExternalEvent.hostName !== 'Outlook Calendar' &&
                          selectedExternalEvent.hostName !== 'Microsoft Calendar' && (
                            <div className="cal-participant-sub" style={{ marginTop: '2px' }}>
                              <span>Host: {selectedExternalEvent.hostName}</span>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Details if present */}
                {selectedExternalEvent.location && (
                  <div className="cal-section">
                    <h4 className="cal-section-label">Location / Meeting URL</h4>
                    <div className="ext-event-location-box">
                      <i className="fas fa-map-marker-alt"></i>
                      <span>{selectedExternalEvent.location}</span>
                    </div>
                  </div>
                )}

                {selectedExternalEvent.description && (
                  <div className="cal-section">
                    <h4 className="cal-section-label">Event Description / Notes</h4>
                    <div className="cal-note-quote">
                      <p className="info-notes-quote">{selectedExternalEvent.description}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="cal-modal-footer">
              <span className="ext-footer-note">
                <i className="fas fa-shield-alt"></i> Read-only synced event from external calendar
              </span>
              <div className="cal-footer-right-actions">
                <button
                  type="button"
                  className="cal-btn-primary"
                  onClick={() => setSelectedExternalEvent(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
